import { ParagraphTextRange } from 'src/models/Text'

/**
 * Slate pads empty leaves and line ends with a zero-width placeholder text node. It is invisible and is
 * not part of the paragraph's content, so counting it would shift every offset after it.
 */
const SLATE_ZERO_WIDTH_ATTRIBUTE = 'data-slate-zero-width'

const PARAGRAPH_ID_ATTRIBUTE = 'data-paragraph-id'

const isContentTextNode = (node: Node): boolean => {
    const parent = node.parentElement

    return !!parent && !parent.hasAttribute(SLATE_ZERO_WIDTH_ATTRIBUTE)
}

const contentTextWalker = (paragraphElement: Element) =>
    document.createTreeWalker(paragraphElement, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => (isContentTextNode(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT),
    })

// Walk text nodes (not child indexes) so readonly and Slate trees resolve the same flat offsets.
// Returns null for stale/out-of-range offsets — clamping would paint the wrong words.
export const resolveHighlightRange = (paragraphElement: Element, { start, end }: ParagraphTextRange): Range | null => {
    if (start < 0 || end <= start) {
        return null
    }

    const walker = contentTextWalker(paragraphElement)

    let consumed = 0
    let startNode: Node | null = null
    let startNodeOffset = 0
    let currentNode = walker.nextNode()

    while (currentNode) {
        const length = currentNode.nodeValue?.length ?? 0

        // Boundary starts belong to the next node (`>`), so the range begins on a painted character.
        if (!startNode && consumed + length > start) {
            startNode = currentNode
            startNodeOffset = start - consumed
        }

        if (startNode && consumed + length >= end) {
            return buildRange(startNode, startNodeOffset, currentNode, end - consumed)
        }

        consumed += length
        currentNode = walker.nextNode()
    }

    return null
}

const buildRange = (startNode: Node, startOffset: number, endNode: Node, endOffset: number): Range | null => {
    try {
        const range = document.createRange()
        range.setStart(startNode, startOffset)
        range.setEnd(endNode, endOffset)

        return range
    } catch {
        return null
    }
}

export const paragraphElementSelector = (paragraphId: string) => `[${PARAGRAPH_ID_ATTRIBUTE}="${CSS.escape(paragraphId)}"]`

export const closestParagraphElement = (node: Node): Element | null =>
    (node instanceof Element ? node : node.parentElement)?.closest(`[${PARAGRAPH_ID_ATTRIBUTE}]`) ?? null

export const resolveRangesIn = (container: HTMLElement, ranges: ParagraphTextRange[]): Range[] =>
    ranges
        .map((range) => {
            const paragraphElement = container.querySelector(paragraphElementSelector(range.paragraphId))

            return paragraphElement ? resolveHighlightRange(paragraphElement, range) : null
        })
        .filter((range): range is Range => range !== null)

type ParagraphPoint = { paragraphId: string; offset: number }

// The inverse of `resolveHighlightRange`: a DOM point to a char offset in its paragraph.
export const resolveParagraphPoint = (node: Node, offset: number): ParagraphPoint | null => {
    const paragraphElement = closestParagraphElement(node)
    const paragraphId = paragraphElement?.getAttribute(PARAGRAPH_ID_ATTRIBUTE)
    const isInsideParagraph = paragraphElement !== null && !!paragraphId
    if (!isInsideParagraph) {
        return null
    }

    const point = document.createRange()
    try {
        point.setStart(node, offset)
    } catch {
        return null
    }

    const walker = contentTextWalker(paragraphElement)
    let consumed = 0
    for (let current = walker.nextNode(); current; current = walker.nextNode()) {
        if (current === node) {
            return { paragraphId, offset: consumed + offset }
        }
        // The point is before this node (e.g. in a skipped zero-width node): use this node's start.
        if (point.comparePoint(current, 0) >= 0) {
            break
        }
        consumed += current.nodeValue?.length ?? 0
    }

    return { paragraphId, offset: consumed }
}

export const paragraphPointAt = (x: number, y: number): ParagraphPoint | null => {
    // feature-detect.js guarantees caretRangeFromPoint before the app starts.
    const range = document.caretRangeFromPoint(x, y)

    return range && resolveParagraphPoint(range.startContainer, range.startOffset)
}
